// Shared WS types + normalizers

export type ConnectionStatus = "connecting" | "open" | "closed" | "error";

export type ProfileVisibility = "public" | "followers" | "private";

export interface UserSettings {
  profile_visibility: ProfileVisibility;
  show_achievements: boolean;
  allow_messages: boolean;
  email_enabled: boolean;
  push_enabled: boolean;
  push_notify_follower: boolean;
  push_notify_market_events: boolean;
  push_notify_achievement: boolean;
  email_notify_follower: boolean;
  email_notify_market_events: boolean;
  email_notify_achievement: boolean;
  created_at?: string;
  updated_at?: string;
}

export type AddressType = "residential" | "invoice";
export interface Address {
  id: string;
  address_type: AddressType;
  recipient_name?: string | null;
  address_line1?: string | null;
  street2?: string | null;
  city?: string | null;
  state_province?: string | null;
  postalcode?: string | null;
  country?: string | null;
  country_code?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface CompanyProfile {
  company_name: string;
  vat_id?: string | null;
  registration_number?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface TradesListData {
  pagination: { total: number; limit: number; offset: number };
  items: any[]; // replace with your concrete Trade[] later
}

export type PayoutMethodType = "wire" | "paypal" | "crypto";
export type PayoutMethodDetails =
  | { type: "wire"; bank_name?: string | null; bic?: string | null; iban_last4?: string | null; iban_masked?: string | null }
  | { type: "paypal"; email_masked: string }
  | { type: "crypto"; crypto_currency: string; crypto_network: string; address_masked: string };

export interface PayoutMethod {
  id: string;
  method_type: PayoutMethodType;
  currency?: string;
  is_default: boolean;
  created_at?: string;
  updated_at?: string;
  details: PayoutMethodDetails | Record<string, never>;
}

export interface User {
  id: string;
  username: string;
  community_username?: string | null;
  display_name?: string | null;
  email: string;
  firstname?: string | null;
  lastname?: string | null;
  created_at?: string;
  updated_at?: string;
  deactivated_at?: string | null;
  status: string;
  is_blacklisted: boolean;
  blacklist_reason?: string | null;
  blacklisted_at?: string | null;
  is_trader: boolean;
  is_affiliate: boolean;
  is_partner: boolean;
  is_staff: boolean;
  staff_role?: string | null;
  provider_id?: string | null;
  referred_by_id?: string | null;
  is_company: boolean;
  notes?: string | null;
  language_code: string;
  timezone: string;
  avatar_file_id?: string | number;
  settings?: UserSettings | null;
  addresses: Address[];
  company?: CompanyProfile | null;
  payout_methods: PayoutMethod[];
  is_auth_app_2fa_enabled?: boolean;
  _displayNameEffective?: string;
}

/** Trading Accounts */
export interface TradingAccountDailyStats {
  accountId: string;
  date: string;
  balance?: number;
  equity?: number;
  profit_loss?: number;
  trades_count?: number;
  winning_trades?: number;
  losing_trades?: number;
  [key: string]: any;
}

export interface TradingAccount {
  id: string;
  userId: string;
  accountIdentifier: string;
  status: "Active" | "Inactive" | "Suspended" | string;
  accountType: "Evaluation" | "Funded" | string;
  createdAt?: string;
  createdAtUtc?: string;
  expiresAt?: string | null;
  expiresAtUtc?: string | null;
  providerId?: string | null;
  updatedAt?: string | null;
  name?: string;
  accountSize?: string;
  backendSystemId?: string;
  backendSystemName?: string;
  templateId?: string;
  config?: Record<string, any>;
  dailyStats?: TradingAccountDailyStats | null;
  statsTotal?: Record<string, any>;
  statsDaily?: any[];
  statsDailyLatest?: Record<string, any>;
  balanceHistory?: any[];
  drawdownHistory?: any[];
  deactivatedAt?: string | null;
  deactivatedAtUtc?: string | null;
  deactivationReason?: string | null;
  ruleViolations?: {
    count?: number;
    latest?: {
      id?: string;
      rule_type_key?: string;
      occurred_at?: string;
      occurred_at_utc?: string;
      message?: string;
    };
  } | null;
  accountBalance?: number;
  accountEquity?: number;
}

/** Funded Payouts */
export interface FundedPayout {
  id: string;
  userId: string;
  providerId: string;
  tradingAccountId: string;
  accountIdentifier?: string | null;
  requestAmount: string;
  payoutAmount: string;
  status: "Pending" | "Completed" | "Rejected" | "Canceled" | string;
  requestedAt?: string;
  processedAt?: string;
  rejectionReason?: string | null;
  receiptNumber?: string | null;
  receiptFileId?: string | null;
  certificateFileId?: string | null;
  payoutMethod?: {
    method_type: PayoutMethodType;
    details?: any;
  } | null;
}

export interface WalletSummaryBalance {
  currency: string;
  posted_balance: string;
  pending_total: string;
  available_balance: string;
}

export interface InvoiceOrderItem {
  product_name: string;
  quantity: number;
}

export interface Invoice {
  invoiceId: string;
  providerId: string;
  identityId: string;
  currency: string;
  totalAmount: number;
  status: string;
  invoiceNumber: string;
  invoicePublicId: string;
  createdAt: string;
  file_id: string | null;
  order_items?: InvoiceOrderItem[];
}

// Levels
export type SeasonType = "global" | "seasonal";
export interface LevelRank {
  id: string;
  rank_code: string;
  display_name: string;
  min_level: number;
}
export interface LevelState {
  user_id: string;
  season_id: string;
  total_xp: number;
  level: number;
  formula_id: string;
  last_event_at?: string | null;
  provider_id: string;
  season_key: string;
  name: string;
  season_type: SeasonType;
  is_active: boolean;
  finalized: boolean;
  next_level_cost?: number | null;
  xp_into_level?: number | null;
  xp_to_next?: number | null;
  xp_progress_ratio?: number | null;
  current_rank: LevelRank | null;
}
export interface LevelStatesListData { items: LevelState[]; }
export interface LevelSeason {
  id: string; provider_id: string; season_key: string; name: string;
  season_type: SeasonType; is_active: boolean; finalized: boolean;
  season_start?: string | null; season_end?: string | null; formula_id: string;
}
export interface LevelRankTierRewards {
  badge?: string;
  credits_usd?: number;
  discount_one_time_pct?: number;
  free_reset?: boolean;
  free_extension?: boolean;
  trading_fee_usd?: number;
  eval_drawdown_reduction_pct?: number;
}

export interface LevelRankTier {
  id: string; season_id: string; rank_code: string; display_name: string;
  sort_order: number; min_level: number; badge_url?: string | null; color_hex?: string | null; rewards?: LevelRankTierRewards | string | null;
}
export interface LevelSeasonsListData { items: LevelSeason[]; }
export interface LevelRankTiersListData { items: LevelRankTier[]; }

export interface LevelEvent {
  id: string;
  user_id: string;
  season_id: string;
  event_type: string;
  xp_delta: number;
  new_total_xp: number;
  new_level: number;
  metadata?: any;
  created_at: string;
}
export interface LevelEventsListData { items: LevelEvent[]; pagination?: { total: number; limit: number; offset: number }; }

// Achievements
export interface AchievementsListData {
  pagination: { total: number; limit: number; offset: number };
  items: Array<{
    id: string; provider_id: string; achievement_key: string;
    rarity_code: string; rarity_name: string; rarity_order: number | string;
    rarity_color?: string | null; name: string; description: string;
    points: number | string; icon_url?: string | null;
    is_active: boolean; progress_target?: number | null;
    user_id?: string | null; unlocked_at?: string | null; progress_current?: number | null;
    unlocked: boolean; user_status: "unlocked" | "ready" | "in_progress" | "locked" | string;
    progress_ratio?: number | null;
  }>;
}
export type AchievementLite = {
  id: string;
  key: string;
  unlockedAt: string;
  achievement_key: string;
  rarity_code: string;
  rarity_name: string;
  rarity_order: string;
  rarity_color: string | null;
  name: string;
  description: string;
  points: number;
  icon_url: string | null;
  is_active: boolean;
  progress_target: number;
  user_id: string | null;
  unlocked_at: string | null;
  progress_current: number | null;
  unlocked: boolean;
  user_status: string;
  progress_ratio: number;
};


// Helpers
export type OutboxEvent = {
  topic: string;
  event: string;
  seq: number;
  occurred_at: string;
  keys: Record<string, any>;
  data: any;
  schema_version?: number;
};

export const isObject = (v: any): v is Record<string, any> => v !== null && typeof v === "object" && !Array.isArray(v);
const toNum = (n: any): number => (n === null || n === undefined || n === "" ? 0 : (Number.isFinite(Number(n)) ? Number(n) : 0));
const toNumOrNull = (n: any): number | null => (n === null || n === undefined || n === "" ? null : (Number.isFinite(Number(n)) ? Number(n) : null));

export type PayoutMethodDetailsUnion = PayoutMethodDetails | Record<string, never>;

export function normalizePayoutDetails(method_type: PayoutMethodType, details: any): PayoutMethodDetailsUnion {
  if (!isObject(details)) return {};
  if (method_type === "wire") {
    return { type: "wire", bank_name: details.bank_name ?? null, bic: details.bic ?? null, iban_last4: details.iban_last4 ?? null, iban_masked: details.iban_masked ?? null };
  }
  if (method_type === "paypal") return { type: "paypal", email_masked: details.email_masked ?? "" };
  if (method_type === "crypto") return { type: "crypto", crypto_currency: details.crypto_currency ?? "", crypto_network: details.crypto_network ?? "", address_masked: details.address_masked ?? "" };
  return {};
}

export function normalizeUser(input: any): User {
  const u = (isObject(input) ? input : {}) as Record<string, any>;

  const payout_methods: PayoutMethod[] = Array.isArray(u.payout_methods)
    ? u.payout_methods.map((pm: any) => {
        const method_type = (pm?.method_type ?? "wire") as PayoutMethodType;
        return {
          id: String(pm?.id),
          method_type,
          currency: pm?.currency ?? undefined,
          is_default: Boolean(pm?.is_default),
          created_at: pm?.created_at ?? undefined,
          updated_at: pm?.updated_at ?? undefined,
          details: normalizePayoutDetails(method_type, pm?.details),
        };
      })
    : [];
  const addresses: Address[] = Array.isArray(u.addresses)
    ? u.addresses.map((a: any) => ({
        id: String(a?.id),
        address_type: String(a?.address_type ?? "residential") as AddressType,
        recipient_name: a?.recipient_name ?? null,
        address_line1: a?.street ?? a?.address_line1 ?? null,
        street2: a?.street2 ?? null,
        city: a?.city ?? null,
        state_province: a?.state_province ?? null,
        postalcode: a?.postal_code ?? a?.postalcode ?? null,
        country: a?.country ?? null,
        country_code: a?.country_code ?? null,
        created_at: a?.created_at ?? undefined,
        updated_at: a?.updated_at ?? undefined,
      }))
    : [];
  const settings: UserSettings | null = isObject(u.settings)
    ? {
        profile_visibility: u.settings.profile_visibility ?? "private",
        show_achievements: !!u.settings.show_achievements,
        allow_messages: !!u.settings.allow_messages,
        email_enabled: !!u.settings.email_enabled,
        push_enabled: !!u.settings.push_enabled,
        push_notify_follower: !!u.settings.push_notify_follower,
        push_notify_market_events: !!u.settings.push_notify_market_events,
        push_notify_achievement: !!u.settings.push_notify_achievement,
        email_notify_follower: !!u.settings.email_notify_follower,
        email_notify_market_events: !!u.settings.email_notify_market_events,
        email_notify_achievement: !!u.settings.email_notify_achievement,
        created_at: u.settings.created_at,
        updated_at: u.settings.updated_at,
      }
    : null;

  const displayNameEff =
    (u.display_name && String(u.display_name).trim()) ||
    [u.firstname, u.lastname].filter(Boolean).join(" ").trim() ||
    u.username ||
    u.email;
  return {
    id: String(u.id),
    username: String(u.username ?? ""),
    community_username: u.community_username ?? null,
    display_name: u.display_name ?? null,
    email: String(u.email ?? ""),
    firstname: u.firstname ?? null,
    lastname: u.lastname ?? null,
    created_at: u.created_at,
    updated_at: u.updated_at,
    deactivated_at: u.deactivated_at ?? null,
    status: String(u.status ?? ""),
    is_blacklisted: !!u.is_blacklisted,
    blacklist_reason: u.blacklist_reason ?? null,
    blacklisted_at: u.blacklisted_at ?? null,
    is_trader: !!u.is_trader,
    is_affiliate: !!u.is_affiliate,
    is_partner: !!u.is_partner,
    is_staff: !!u.is_staff,
    staff_role: u.staff_role ?? null,
    provider_id: u.provider_id ?? null,
    referred_by_id: u.referred_by_id ?? null,
    is_company: !!u.is_company,
    notes: u.notes ?? null,
    language_code: String(u.language_code ?? "en"),
    timezone: String(u.timezone ?? "UTC"),
    avatar_file_id: u.avatar_file_id ?? null,
    settings,
    addresses,
    company: isObject(u.company)
      ? {
          company_name: String(u.company.company_name ?? ""),
          vat_id: u.company.vat_id ?? null,
          registration_number: u.company.registration_number ?? null,
          created_at: u.company.created_at,
          updated_at: u.company.updated_at,
        }
      : null,
    payout_methods,
    is_auth_app_2fa_enabled: !!u.is_auth_app_2fa_enabled,
    _displayNameEffective: displayNameEff,
  };
}

export function normalizeTradingAccount(input: any): TradingAccount {
  const a = (isObject(input) ? input : {}) as Record<string, any>;
  const id = String(a.id);
  const userId = String(a.user_id ?? "");
  const accountIdentifier = String(a.account_identifier ?? "");
  const status = String(a.status ?? "");
  const accountType = String(a.account_type ?? "");
  const createdAt = a.created_at ?? undefined;
  const expiresAt = a.expires_at ?? null;
  return {
    id, userId, accountIdentifier, status, accountType, createdAt, expiresAt,
    providerId: a.provider_id ?? null,
    updatedAt: a.updated_at ?? null,
    name: accountIdentifier || id,
    accountSize: a.account_size ?? undefined,
    backendSystemId: a.backend_system_id ?? undefined,
    backendSystemName: a.backend_system_name ?? undefined,
    templateId: a.template_id ?? undefined,
    config: a.config ?? undefined,
    statsTotal: a.stats_total ?? undefined,
    statsDaily: a.stats_daily ?? undefined,
    statsDailyLatest: a.stats_daily_latest ?? undefined,
    balanceHistory: a.balance_history ?? undefined,
    drawdownHistory: a.drawdown_history ?? undefined,
    deactivatedAt: a.deactivated_at ?? null,
    deactivationReason: a.deactivation_reason ?? null,
    ruleViolations: a.rule_violations ?? null,
    accountBalance: a.account_balance ?? a.stats_total?.account_balance ?? undefined,
    accountEquity: a.account_equity ?? undefined,
  };
}
export function normalizeTradingAccounts(list: any[]): TradingAccount[] {
  if (!Array.isArray(list)) return [];
  return list
    .filter(item => (item?.deactivation_reason ?? item?.deactivationReason) !== 'Reset')
    .map(normalizeTradingAccount);
}
export function normalizeFundedPayout(input: any): FundedPayout {
  const p = (isObject(input) ? input : {}) as Record<string, any>;
  return {
    id: String(p.id),
    userId: String(p.user_id ?? p.identity_id ?? ""),
    providerId: String(p.provider_id ?? ""),
    tradingAccountId: String(p.trading_account_id ?? ""),
    accountIdentifier: p.account_identifier ?? null,
    requestAmount: String(p.request_amount ?? "0"),
    payoutAmount: String(p.payout_amount ?? "0"),
    status: String(p.status ?? ""),
    requestedAt: p.requested_at ?? undefined,
    processedAt: p.processed_at ?? undefined,
    rejectionReason: p.rejection_reason ?? null,
    receiptNumber: p.receipt_number ?? null,
    receiptFileId: p.receipt_file_id ?? null,
    certificateFileId: p.certificate_file_id ?? null,
    payoutMethod: p.payout_method ? {
      method_type: p.payout_method.method_type ?? "wire",
      details: p.payout_method.details ?? null
    } : null,
  };
}
export function normalizeFundedPayouts(list: any[]): FundedPayout[] {
  if (!Array.isArray(list)) return [];
  return list.map(normalizeFundedPayout);
}
export function mapAchievementListToLite(items: AchievementsListData["items"]): AchievementLite[] {
  return (items || []).map(it => ({
    id: String(it.id),
    key: String(it.achievement_key),
    unlockedAt: it.unlocked_at ?? "",
    achievement_key: String(it.achievement_key ?? ""),
    rarity_code: String(it.rarity_code ?? "common"),
    rarity_name: String(it.rarity_name ?? "Common"),
    rarity_order: String(it.rarity_order ?? "0"),
    rarity_color: it.rarity_color ?? null,
    name: String(it.name ?? ""),
    description: String(it.description ?? ""),
    points: toNum(it.points),
    icon_url: it.icon_url ?? null,
    is_active: Boolean(it.is_active),
    progress_target: toNum(it.progress_target),
    user_id: it.user_id ?? null,
    unlocked_at: it.unlocked_at ?? null,
    progress_current: toNumOrNull(it.progress_current),
    unlocked: Boolean(it.unlocked),
    user_status: String(it.user_status ?? "locked"),
    progress_ratio: toNum(it.progress_ratio)
  }));
}

// Levels
export function normalizeLevelRank(input: any): LevelRank {
  const r = isObject(input) ? input : {};
  return { id: String((r as any).id ?? ""), rank_code: String((r as any).rank_code ?? ""), display_name: String((r as any).display_name ?? ""), min_level: toNum((r as any).min_level) };
}
export function normalizeLevelState(input: any): LevelState {
  const x = isObject(input) ? input : {};
  const rankRaw = (x as any).current_rank ?? null;
  return {
    user_id: String((x as any).user_id ?? ""),
    season_id: String((x as any).season_id ?? ""),
    total_xp: toNum((x as any).total_xp),
    level: toNum((x as any).level),
    formula_id: String((x as any).formula_id ?? ""),
    last_event_at: (x as any).last_event_at ?? null,
    provider_id: String((x as any).provider_id ?? ""),
    season_key: String((x as any).season_key ?? ""),
    name: String((x as any).name ?? ""),
    season_type: ((x as any).season_type ?? "global") as SeasonType,
    is_active: !!(x as any).is_active,
    finalized: !!(x as any).finalized,
    next_level_cost: toNumOrNull((x as any).next_level_cost),
    xp_into_level: toNumOrNull((x as any).xp_into_level),
    xp_to_next: toNumOrNull((x as any).xp_to_next),
    xp_progress_ratio: (x as any).xp_progress_ratio === null || (x as any).xp_progress_ratio === undefined ? null : Number((x as any).xp_progress_ratio),
    current_rank: rankRaw ? normalizeLevelRank(rankRaw) : null,
  };
}

export function normalizeLevelProgressUpdate(input: any): Partial<LevelState> & { season_id: string } {
  const x = isObject(input) ? input : {};
  const patch: Record<string, any> = {
    season_id: String((x as any).season_id ?? ""),
  };
  if ((x as any).identity_id != null) patch.user_id = String((x as any).identity_id);
  if ((x as any).user_id != null) patch.user_id = String((x as any).user_id);
  if ((x as any).level != null) patch.level = toNum((x as any).level);
  if ((x as any).total_xp != null) patch.total_xp = toNum((x as any).total_xp);
  if ((x as any).provider_id != null) patch.provider_id = String((x as any).provider_id);
  if ((x as any).last_event_at != null) patch.last_event_at = (x as any).last_event_at;
  if ((x as any).next_level_cost != null) patch.next_level_cost = toNumOrNull((x as any).next_level_cost);
  if ((x as any).xp_into_level != null) patch.xp_into_level = toNumOrNull((x as any).xp_into_level);
  if ((x as any).xp_to_next != null) patch.xp_to_next = toNumOrNull((x as any).xp_to_next);
  if ((x as any).xp_progress_ratio != null) patch.xp_progress_ratio = Number((x as any).xp_progress_ratio);
  if ((x as any).season_name != null && !patch.name) patch.name = String((x as any).season_name);
  if ((x as any).name != null) patch.name = String((x as any).name);
  const rankRaw = (x as any).current_rank ?? null;
  if (rankRaw) {
    patch.current_rank = normalizeLevelRank(rankRaw);
  } else if ((x as any).rank_code != null || (x as any).rank_name != null) {
    patch.current_rank = {
      id: String((x as any).current_rank_id ?? ""),
      rank_code: String((x as any).rank_code ?? ""),
      display_name: String((x as any).rank_name ?? ""),
      min_level: 0,
    };
  }
  const lxp = (x as any).level_xp;
  if (isObject(lxp)) {
    if (patch.xp_into_level == null && (lxp as any).current != null) patch.xp_into_level = toNum((lxp as any).current);
    if (patch.next_level_cost == null && (lxp as any).required != null) patch.next_level_cost = toNum((lxp as any).required);
    if (patch.xp_to_next == null && (lxp as any).remaining != null) patch.xp_to_next = toNum((lxp as any).remaining);
  }
  return patch as Partial<LevelState> & { season_id: string };
}
export function normalizeLevelSeason(input: any): LevelSeason {
  const s = isObject(input) ? input : {};
  return {
    id: String((s as any).id ?? ""),
    provider_id: String((s as any).provider_id ?? ""),
    season_key: String((s as any).season_key ?? ""),
    name: String((s as any).name ?? ""),
    season_type: (((s as any).season_type) ?? "global") as SeasonType,
    is_active: !!(s as any).is_active,
    finalized: !!(s as any).finalized,
    season_start: (s as any).season_start ?? null,
    season_end: (s as any).season_end ?? null,
    formula_id: String((s as any).formula_id ?? ""),
  };
}
export function normalizeLevelRankTier(input: any): LevelRankTier {
  const t = isObject(input) ? input : {};
  let rewards: LevelRankTierRewards | null = null;
  const rawRewards = (t as any).rewards;
  if (typeof rawRewards === 'string') {
    try { rewards = JSON.parse(rawRewards); } catch { rewards = null; }
  } else if (isObject(rawRewards)) {
    rewards = rawRewards as LevelRankTierRewards;
  }
  return {
    id: String((t as any).id ?? ""),
    season_id: String((t as any).season_id ?? ""),
    rank_code: String((t as any).rank_code ?? ""),
    display_name: String((t as any).display_name ?? ""),
    sort_order: toNum((t as any).sort_order),
    min_level: toNum((t as any).min_level),
    badge_url: (t as any).badge_url ?? null,
    color_hex: (t as any).color_hex ?? null,
    rewards,
  };
}

export interface PartnerTier {
  id: string;
  tier_code: string;
  display_name: string;
  sort_order: number;
  min_total_sales_count: number;
  min_total_revenue: number;
  min_active_referrals: number;
  commission_bonus_percent: number;
  one_time_bonus_amount: number | null;
  benefits: string[];
  is_active: boolean;
}

export interface PartnerPerformance {
  current_tier_id: string;
  current_tier: {
    tier_code: string;
    display_name: string;
    commission_bonus_percent: number;
  };
  total_sales_count: number;
  total_revenue: number;
  active_referrals_count: number;
  lifetime_sales_count: number;
  lifetime_revenue: number;
}

export interface PartnerProgressToNextTier {
  next_tier_id: string;
  next_tier_code: string;
  next_tier_display_name: string;
  current_revenue: number;
  target_revenue: number;
  progress_percent: number;
}

export interface PartnerCommissionSchedule {
  default_rate: number;
  current_tier_code: string;
  current_tier_bonus: number;
  total_with_tier_bonus: number;
}

export interface PartnerData {
  id: string;
  provider_id: string;
  user_id: string;
  handle: string;
  partner_display_name: string;
  status: string;
  default_commission_percent: string;
  notes: string;
  meta: any;
  created_at: string;
  updated_at: string;
  tiers: PartnerTier[];
  performance: PartnerPerformance | null;
  progress_to_next_tier: PartnerProgressToNextTier | null;
  commission_schedule: PartnerCommissionSchedule | null;
}

export interface PartnerLinkStatistics {
  clicks_count: number;
  revenue_total: number;
  commission_total: number;
  transactions_count: number;
  conversion_rate: number;
}

export interface PartnerLinkMeta {
  name: string;
  category: string;
  description: string;
}

export interface PartnerLink {
  id: string;
  provider_id: string;
  partner_id: string;
  suffix: string;
  is_active: boolean;
  cookie_ttl_days: string;
  meta: PartnerLinkMeta;
  created_at: string;
  updated_at: string;
  link: string;
  category: string;
  statistics: PartnerLinkStatistics;
}

export interface PartnerCodeStatistics {
  usage_count: number;
  revenue_total: number;
  commission_total: number;
  transactions_count: number;
  conversion_rate: number;
}

export interface PartnerCodeMeta {
  name: string;
  category: string;
  description: string;
}

export interface PartnerCode {
  id: string;
  provider_id: string;
  partner_id: string;
  code: string;
  is_active: boolean;
  discount_type: string;
  discount_value: string;
  meta: PartnerCodeMeta;
  created_at: string;
  updated_at: string;
  used_count: string;
  statistics: PartnerCodeStatistics;
}

export function normalizePartnerLink(input: any): PartnerLink {
  const p = (isObject(input) ? input : {}) as Record<string, any>;
  return {
    id: String(p.id ?? ""),
    provider_id: String(p.provider_id ?? ""),
    partner_id: String(p.partner_id ?? ""),
    suffix: String(p.suffix ?? ""),
    is_active: Boolean(p.is_active ?? true),
    cookie_ttl_days: String(p.cookie_ttl_days ?? "30"),
    meta: isObject(p.meta) ? {
      name: String(p.meta.name ?? ""),
      category: String(p.meta.category ?? ""),
      description: String(p.meta.description ?? "")
    } : { name: "", category: "", description: "" },
    created_at: String(p.created_at ?? ""),
    updated_at: String(p.updated_at ?? ""),
    link: String(p.link ?? ""),
    category: String(p.category ?? ""),
    statistics: isObject(p.statistics) ? {
      clicks_count: toNum(p.statistics.clicks_count),
      revenue_total: toNum(p.statistics.revenue_total),
      commission_total: toNum(p.statistics.commission_total),
      transactions_count: toNum(p.statistics.transactions_count),
      conversion_rate: toNum(p.statistics.conversion_rate)
    } : { clicks_count: 0, revenue_total: 0, commission_total: 0, transactions_count: 0, conversion_rate: 0 }
  };
}

export function normalizePartnerLinks(list: any[]): PartnerLink[] {
  if (!Array.isArray(list)) return [];
  return list.map(normalizePartnerLink);
}

export function normalizePartnerCode(input: any): PartnerCode {
  const c = (isObject(input) ? input : {}) as Record<string, any>;
  return {
    id: String(c.id ?? ""),
    provider_id: String(c.provider_id ?? ""),
    partner_id: String(c.partner_id ?? ""),
    code: String(c.code ?? ""),
    is_active: Boolean(c.is_active ?? true),
    discount_type: String(c.discount_type ?? ""),
    discount_value: String(c.discount_value ?? "0"),
    meta: isObject(c.meta) ? {
      name: String(c.meta.name ?? ""),
      category: String(c.meta.category ?? ""),
      description: String(c.meta.description ?? "")
    } : { name: "", category: "", description: "" },
    created_at: String(c.created_at ?? ""),
    updated_at: String(c.updated_at ?? ""),
    used_count: String(c.used_count ?? "0"),
    statistics: isObject(c.statistics) ? {
      used_count: String(c.statistics.used_count ?? "0"),
      revenue_total: toNum(c.statistics.revenue_total),
      commission_total: toNum(c.statistics.commission_total),
      transactions_count: toNum(c.statistics.transactions_count),
      conversion_rate: toNum(c.statistics.conversion_rate)
    } : { used_count: "0", revenue_total: 0, commission_total: 0, transactions_count: 0, conversion_rate: 0 }
  };
}

export function normalizePartnerCodes(list: any[]): PartnerCode[] {
  if (!Array.isArray(list)) return [];
  return list.map(normalizePartnerCode);
}

function normalizePartnerTier(input: any): PartnerTier {
  const t = (isObject(input) ? input : {}) as Record<string, any>;
  let benefits: string[] = [];
  if (typeof t.benefits === 'string') {
    try { benefits = JSON.parse(t.benefits); } catch { benefits = []; }
  } else if (Array.isArray(t.benefits)) {
    benefits = t.benefits;
  }
  return {
    id: String(t.id ?? ""),
    tier_code: String(t.tier_code ?? ""),
    display_name: String(t.display_name ?? ""),
    sort_order: toNum(t.sort_order),
    min_total_sales_count: toNum(t.min_total_sales_count),
    min_total_revenue: toNum(t.min_total_revenue),
    min_active_referrals: toNum(t.min_active_referrals),
    commission_bonus_percent: toNum(t.commission_bonus_percent),
    one_time_bonus_amount: t.one_time_bonus_amount != null ? toNum(t.one_time_bonus_amount) : null,
    benefits,
    is_active: Boolean(t.is_active ?? true),
  };
}

export function normalizePartnerData(input: any): PartnerData {
  const p = (isObject(input) ? input : {}) as Record<string, any>;

  let meta = {};
  if (typeof p.meta === 'string') {
    try { meta = JSON.parse(p.meta); } catch { meta = {}; }
  } else if (isObject(p.meta)) {
    meta = p.meta;
  }

  const tiers = Array.isArray(p.tiers) ? p.tiers.map(normalizePartnerTier) : [];

  const perf = isObject(p.performance) ? p.performance as Record<string, any> : null;
  const performance: PartnerPerformance | null = perf ? {
    current_tier_id: String(perf.current_tier_id ?? ""),
    current_tier: isObject(perf.current_tier) ? {
      tier_code: String(perf.current_tier.tier_code ?? ""),
      display_name: String(perf.current_tier.display_name ?? ""),
      commission_bonus_percent: toNum(perf.current_tier.commission_bonus_percent),
    } : { tier_code: "", display_name: "", commission_bonus_percent: 0 },
    total_sales_count: toNum(perf.total_sales_count),
    total_revenue: toNum(perf.total_revenue),
    active_referrals_count: toNum(perf.active_referrals_count),
    lifetime_sales_count: toNum(perf.lifetime_sales_count),
    lifetime_revenue: toNum(perf.lifetime_revenue),
  } : null;

  const prog = isObject(p.progress_to_next_tier) ? p.progress_to_next_tier as Record<string, any> : null;
  const progress_to_next_tier: PartnerProgressToNextTier | null = prog ? {
    next_tier_id: String(prog.next_tier_id ?? ""),
    next_tier_code: String(prog.next_tier_code ?? ""),
    next_tier_display_name: String(prog.next_tier_display_name ?? ""),
    current_revenue: toNum(prog.current_revenue),
    target_revenue: toNum(prog.target_revenue),
    progress_percent: toNum(prog.progress_percent),
  } : null;

  const cs = isObject(p.commission_schedule) ? p.commission_schedule as Record<string, any> : null;
  const commission_schedule: PartnerCommissionSchedule | null = cs ? {
    default_rate: toNum(cs.default_rate),
    current_tier_code: String(cs.current_tier_code ?? ""),
    current_tier_bonus: toNum(cs.current_tier_bonus),
    total_with_tier_bonus: toNum(cs.total_with_tier_bonus),
  } : null;

  return {
    id: String(p.id ?? ""),
    provider_id: String(p.provider_id ?? ""),
    user_id: String(p.user_id ?? ""),
    handle: String(p.handle ?? ""),
    partner_display_name: String(p.partner_display_name || p.affiliate_display_name || ""),
    status: String(p.status ?? ""),
    default_commission_percent: String(p.default_commission_percent ?? "0"),
    notes: String(p.notes ?? ""),
    meta: meta,
    created_at: String(p.created_at ?? ""),
    updated_at: String(p.updated_at ?? ""),
    tiers,
    performance,
    progress_to_next_tier,
    commission_schedule,
  };
}

/** Settlements (Payout Reports) */
export interface Settlement {
  id: string;
  provider_id: string;
  partner_id: string;
  currency: string;
  period_start: string;
  period_end: string;
  status: 'open' | 'paid' | 'finalized';
  total_commission: string;
  total_items: string;
  created_at: string;
  finalized_at: string | null;
  paid_at: string | null;
  updated_at: string;
  settlement_file_id?: string | null;
  meta?: Record<string, any>;
}

export function normalizeSettlement(s: any): Settlement {
  return {
    id: s.id || '',
    provider_id: s.provider_id || '',
    partner_id: s.partner_id || '',
    currency: s.currency || 'EUR',
    period_start: s.period_start || '',
    period_end: s.period_end || '',
    status: s.status || 'open',
    total_commission: s.total_commission || '0',
    total_items: s.total_items || '0',
    created_at: s.created_at || '',
    finalized_at: s.finalized_at || null,
    paid_at: s.paid_at || null,
    updated_at: s.updated_at || '',
    settlement_file_id: s.settlement_file_id || null,
    meta: s.meta || {}
  };
}

export function normalizeSettlements(list: any[]): Settlement[] {
  if (!Array.isArray(list)) return [];
  return list.map(normalizeSettlement);
}

/** Tickets */
export interface Ticket {
  ticket_id: string;
  provider_id: string;
  created_by_user_id: string;
  assignee_user_id: string | null;
  assignee_name: string | null;
  assignee_department_name: string | null;
  subject: string;
  status: 'open' | 'in_progress' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: string;
  source?: string;
  conversation_id: string;
  is_private?: boolean;
  meta?: any;
  created_at: string;
  updated_at: string;
}

export function normalizeTicket(t: any): Ticket {
  return {
    ticket_id: t.ticket_id || t.id || '',
    provider_id: t.provider_id || '',
    created_by_user_id: t.created_by_user_id || '',
    assignee_user_id: t.assignee_user_id || null,
    assignee_name: t.assignee_name || null,
    assignee_department_name: t.assignee_department_name || null,
    subject: t.subject || '',
    status: t.status || 'open',
    priority: t.priority || 'medium',
    category: t.category || 'general',
    source: t.source,
    conversation_id: t.conversation_id || '',
    is_private: t.is_private,
    meta: t.meta,
    created_at: t.created_at || '',
    updated_at: t.updated_at || ''
  };
}

export function normalizeTickets(list: any[]): Ticket[] {
  if (!Array.isArray(list)) return [];
  return list.map(normalizeTicket);
}

/** Trading Streaks */
export interface TradingStreakDay {
  date: string;
  trades: number;
  net_pnl: number;
}

export interface TradingStreak {
  user_id: string;
  current_streak_days: number;
  best_streak_days: number;
  started: string;
  last_trade: string;
  recent_days: TradingStreakDay[];
  total_trading_days: number;
}

export function normalizeTradingStreak(input: any): TradingStreak | null {
  if (!isObject(input)) return null;
  const s = input as Record<string, any>;

  return {
    user_id: String(s.user_id ?? ''),
    current_streak_days: toNum(s.current_streak_days),
    best_streak_days: toNum(s.best_streak_days),
    started: String(s.started ?? ''),
    last_trade: String(s.last_trade ?? ''),
    recent_days: Array.isArray(s.recent_days) ? s.recent_days.map((day: any) => ({
      date: String(day.date ?? ''),
      trades: toNum(day.trades),
      net_pnl: toNum(day.net_pnl)
    })) : [],
    total_trading_days: toNum(s.total_trading_days)
  };
}

/** Positions */
export interface Position {
  id: string;
  user_id: string;
  trading_account_id: string;
  market_id: string;
  market_symbol: string;
  side: 'Buy' | 'Sell';
  quantity: number;
  entry_price: number;
  current_price?: number;
  unrealized_pnl?: number;
  opened_at: string;
  updated_at?: string;
}

export function normalizePosition(input: any): Position {
  const p = isObject(input) ? input : {};
  return {
    id: String((p as any).id ?? ''),
    user_id: String((p as any).user_id ?? ''),
    trading_account_id: String((p as any).trading_account_id ?? ''),
    market_id: String((p as any).market_id ?? ''),
    market_symbol: String((p as any).market_symbol ?? ''),
    side: (p as any).side ?? 'Buy',
    quantity: toNum((p as any).quantity),
    entry_price: toNum((p as any).entry_price),
    current_price: toNumOrNull((p as any).current_price),
    unrealized_pnl: toNumOrNull((p as any).unrealized_pnl),
    opened_at: String((p as any).opened_at ?? ''),
    updated_at: (p as any).updated_at ?? undefined
  };
}

export function normalizePositions(list: any[]): Position[] {
  if (!Array.isArray(list)) return [];
  return list.map(normalizePosition);
}

/** Providers */
export interface Provider {
  id: string;
  provider_key: string;
  display_name: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export function normalizeProvider(input: any): Provider {
  const p = isObject(input) ? input : {};
  return {
    id: String((p as any).id ?? ''),
    provider_key: String((p as any).provider_key ?? ''),
    display_name: String((p as any).display_name ?? ''),
    is_active: !!(p as any).is_active,
    created_at: (p as any).created_at ?? undefined,
    updated_at: (p as any).updated_at ?? undefined
  };
}

/** Wallet Transactions */
export interface WalletTransaction {
  id: string;
  user_id: string;
  bucket_id: string;
  bucket_key: string;
  amount: string;
  currency: string;
  transaction_type: string;
  category_key: string;
  status: string;
  description: string;
  source: string;
  occurred_at: string;
  created_at: string;
  updated_at?: string;
  metadata?: any;
}

export function normalizeWalletTransaction(input: any): WalletTransaction {
  const w = isObject(input) ? input : {};
  return {
    id: String((w as any).id ?? ''),
    user_id: String((w as any).user_id ?? (w as any).identity_id ?? ''),
    bucket_id: String((w as any).bucket_id ?? ''),
    bucket_key: String((w as any).bucket_key ?? ''),
    amount: String((w as any).amount ?? '0'),
    currency: String((w as any).currency ?? 'EUR'),
    transaction_type: String((w as any).transaction_type ?? ''),
    category_key: String((w as any).category_key ?? ''),
    status: String((w as any).status ?? ''),
    description: String((w as any).description ?? ''),
    source: String((w as any).source ?? ''),
    occurred_at: String((w as any).occurred_at ?? (w as any).created_at ?? ''),
    created_at: String((w as any).created_at ?? ''),
    updated_at: (w as any).updated_at ?? undefined,
    metadata: (w as any).metadata ?? (w as any).meta ?? undefined
  };
}

export function normalizeWalletTransactions(list: any[]): WalletTransaction[] {
  if (!Array.isArray(list)) return [];
  return list.map(normalizeWalletTransaction);
}

export interface WalletBucket {
  id: string;
  user_id: string;
  category_id: string;
  balance: string;
  currency: string;
  created_at: string;
  updated_at?: string;
}

export function normalizeWalletBucket(input: any): WalletBucket {
  const b = isObject(input) ? input : {};
  return {
    id: String((b as any).id ?? ''),
    user_id: String((b as any).user_id ?? ''),
    category_id: String((b as any).category_id ?? ''),
    balance: String((b as any).balance ?? '0'),
    currency: String((b as any).currency ?? 'EUR'),
    created_at: String((b as any).created_at ?? ''),
    updated_at: (b as any).updated_at ?? undefined
  };
}

export interface WalletCategory {
  id: string;
  category_key: string;
  display_name: string;
  description?: string;
  is_active: boolean;
}

export function normalizeWalletCategory(input: any): WalletCategory {
  const c = isObject(input) ? input : {};
  return {
    id: String((c as any).id ?? ''),
    category_key: String((c as any).category_key ?? ''),
    display_name: String((c as any).display_name ?? ''),
    description: (c as any).description ?? undefined,
    is_active: !!(c as any).is_active
  };
}

/** Messages */
export interface Message {
  id: string;
  conversation_id: string;
  sender_user_id: string;
  content: string;
  sent_at: string;
  read_at?: string | null;
  metadata?: any;
}

export function normalizeMessage(input: any): Message {
  const m = isObject(input) ? input : {};
  return {
    id: String((m as any).id ?? ''),
    conversation_id: String((m as any).conversation_id ?? ''),
    sender_user_id: String((m as any).sender_user_id ?? ''),
    content: String((m as any).content ?? ''),
    sent_at: String((m as any).sent_at ?? ''),
    read_at: (m as any).read_at ?? null,
    metadata: (m as any).metadata ?? undefined
  };
}

export interface Conversation {
  id: string;
  participant_user_ids: string[];
  last_message_at?: string;
  created_at: string;
  updated_at?: string;
  metadata?: any;
}

export function normalizeConversation(input: any): Conversation {
  const c = isObject(input) ? input : {};
  return {
    id: String((c as any).id ?? ''),
    participant_user_ids: Array.isArray((c as any).participant_user_ids) ? (c as any).participant_user_ids.map(String) : [],
    last_message_at: (c as any).last_message_at ?? undefined,
    created_at: String((c as any).created_at ?? ''),
    updated_at: (c as any).updated_at ?? undefined,
    metadata: (c as any).metadata ?? undefined
  };
}

/** Partner Commissions */
export interface Commission {
  id: string;
  partner_id: string;
  transaction_id: string;
  commission_amount: string;
  commission_percent: string;
  status: string;
  created_at: string;
  updated_at?: string;
}

export function normalizeCommission(input: any): Commission {
  const c = isObject(input) ? input : {};
  return {
    id: String((c as any).id ?? ''),
    partner_id: String((c as any).partner_id ?? ''),
    transaction_id: String((c as any).transaction_id ?? ''),
    commission_amount: String((c as any).commission_amount ?? '0'),
    commission_percent: String((c as any).commission_percent ?? '0'),
    status: String((c as any).status ?? ''),
    created_at: String((c as any).created_at ?? ''),
    updated_at: (c as any).updated_at ?? undefined
  };
}

export function normalizeCommissions(list: any[]): Commission[] {
  if (!Array.isArray(list)) return [];
  return list.map(normalizeCommission);
}

/** Partner Agreements */
export interface Agreement {
  id: string;
  partner_id: string;
  agreement_type: string;
  status: string;
  signed_at?: string | null;
  created_at: string;
  updated_at?: string;
  metadata?: any;
}

export function normalizeAgreement(input: any): Agreement {
  const a = isObject(input) ? input : {};
  return {
    id: String((a as any).id ?? ''),
    partner_id: String((a as any).partner_id ?? ''),
    agreement_type: String((a as any).agreement_type ?? ''),
    status: String((a as any).status ?? ''),
    signed_at: (a as any).signed_at ?? null,
    created_at: String((a as any).created_at ?? ''),
    updated_at: (a as any).updated_at ?? undefined,
    metadata: (a as any).metadata ?? undefined
  };
}

export function normalizeAgreements(list: any[]): Agreement[] {
  if (!Array.isArray(list)) return [];
  return list.map(normalizeAgreement);
}

/** Notifications */
export interface Notification {
  id: string;
  user_id: string;
  category: string;
  title: string;
  message: string;
  read_at?: string | null;
  created_at: string;
  metadata?: any;
}

export function normalizeNotification(input: any): Notification {
  const n = isObject(input) ? input : {};
  return {
    id: String((n as any).id ?? ''),
    user_id: String((n as any).user_id ?? ''),
    category: String((n as any).category ?? ''),
    title: String((n as any).title ?? ''),
    message: String((n as any).message ?? ''),
    read_at: (n as any).read_at ?? null,
    created_at: String((n as any).created_at ?? ''),
    metadata: (n as any).metadata ?? undefined
  };
}

export function normalizeNotifications(list: any[]): Notification[] {
  if (!Array.isArray(list)) return [];
  return list.map(normalizeNotification);
}

export interface FundedContract {
  id: string;
  status: string;
  contract_number: string;
  contract_created_at: string;
  signed_at: string | null;
  terminated_at: string | null;
  contract_file_id: string | null;
  file_id: string | null;
  passed_account_id: string | null;
  funded_account_id: string | null;
  passed_account_identifier: string | null;
  funded_account_identifier: string | null;
}

export function normalizeFundedContract(input: any): FundedContract {
  const c = isObject(input) ? input : {};
  return {
    id: String((c as any).id ?? ''),
    status: String((c as any).status ?? ''),
    contract_number: String((c as any).contract_number ?? ''),
    contract_created_at: String((c as any).contract_created_at ?? ''),
    signed_at: (c as any).signed_at ?? null,
    terminated_at: (c as any).terminated_at ?? null,
    contract_file_id: (c as any).contract_file_id ?? null,
    file_id: (c as any).file_id ?? null,
    passed_account_id: (c as any).passed_account_id ?? (c as any).passed_trading_account_id ?? null,
    funded_account_id: (c as any).funded_account_id ?? (c as any).funded_trading_account_id ?? null,
    passed_account_identifier: (c as any).passed_account_identifier ?? (c as any).passed_trading_account_identifier ?? null,
    funded_account_identifier: (c as any).funded_account_identifier ?? (c as any).funded_trading_account_identifier ?? null,
  };
}

export function normalizeFundedContracts(list: any[]): FundedContract[] {
  if (!Array.isArray(list)) return [];
  return list.map(normalizeFundedContract);
}

export function normalizeInvoice(input: any): Invoice {
  const i = isObject(input) ? input : {};
  return {
    invoiceId: String((i as any).invoice_id ?? (i as any).invoiceId ?? ''),
    providerId: String((i as any).provider_id ?? (i as any).providerId ?? ''),
    identityId: String((i as any).identity_id ?? (i as any).identityId ?? ''),
    currency: String((i as any).currency ?? ''),
    totalAmount: toNum((i as any).total_gross_amount ?? (i as any).totalAmount ?? 0),
    status: String((i as any).status ?? ''),
    invoiceNumber: String((i as any).invoice_number ?? (i as any).invoiceNumber ?? ''),
    invoicePublicId: String((i as any).invoice_public_number ?? (i as any).invoicePublicId ?? ''),
    createdAt: String((i as any).created_at ?? (i as any).createdAt ?? ''),
    file_id: (i as any).pdf_metadata?.fileId ?? (i as any).file_id ?? null,
    order_items: Array.isArray((i as any).order_items) ? (i as any).order_items : undefined,
  };
}

export interface ActivityFeedItem {
  id: string;
  provider_id: string;
  identity_id: string;
  theme: string;
  activity_type: string;
  occurred_at: string;
  trading_account_id: string | null;
  trade_id: string | null;
  achievement_id: string | null;
  title: string;
  message: string;
  payload: Record<string, any> | null;
  source_service: string;
  created_at: string;
}

